import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  companyApi,
  savedTeamsApi,
  locationApi,
  type LocationRole,
  type LocationWithIntakeLink,
  type TeamMember,
} from '../api/endpoints.ts';
import { ApiRequestError } from '../api/client.ts';
import { useSession } from '../state/session.tsx';
import { Alert, Button, Checkbox, Select, TextInput } from './ui.tsx';
import { formatLocalTime } from '../i18n/datetime.ts';
import { PLANS, USER_ROLES, isPlanId, planAllowsAnotherSeat, type UserRole } from '@shared/constants/index.ts';
import { CheckboxPicker } from './checkbox-picker.tsx';
import { addEmployeesToTeams } from './team-assignment.ts';
import { companyHasPlanFeature } from './plan-gate.tsx';
import { parseWorkDays } from '@shared/scheduling-hours.ts';
import type { Location } from '@shared/types/index.ts';

/**
 * Team management (spec §6.4).
 *
 * Owner-only. Registration only ever creates the first owner, so without this
 * the manager, estimator and read-only roles would exist in the permission
 * model but be unreachable in a running deployment.
 *
 * There is no email delivery yet (spec §21), so the owner sets an initial
 * password and passes it on; the new user changes it from Settings. That
 * becomes a tokenized invitation once a provider is wired in.
 *
 * With two or more active locations, each member also carries the list of
 * locations they may work in (min 1), and optionally a different role at any
 * of them — the company role is the default everywhere, and an override is
 * stored only where one is chosen. Owners cannot be overridden: they are
 * owners at every location. A single-location company never sees any of that
 * — there is nothing to choose between.
 */

/** The roles a member can hold at one location. Never 'owner' — see above. */
const LOCATION_ROLES = ['manager', 'estimator', 'read_only', 'crew'] as const satisfies readonly LocationRole[];

/** Drops override entries for locations the member is no longer assigned to. */
function pruneRoles(roles: Record<string, LocationRole>, locationIds: string[]): Record<string, LocationRole> {
  const pruned: Record<string, LocationRole> = {};
  for (const id of locationIds) {
    const role = roles[id];
    if (role) pruned[id] = role;
  }
  return pruned;
}

export default function TeamSection() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { user, can, currentLocation, company } = useSession();

  const [adding, setAdding] = useState(false);
  const locationRef = useRef(currentLocation?.id);
  locationRef.current = currentLocation?.id;
  const [addingLocationId, setAddingLocationId] = useState<string | undefined>(undefined);
  const [selectedTeams, setSelectedTeams] = useState<string[]>([]);
  const [createdAccount, setCreatedAccount] = useState<TeamMember | null>(null);
  const [search, setSearch] = useState('');
  const [rosterLocation, setRosterLocation] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(0);
  const [bulkPeople, setBulkPeople] = useState<string[]>([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkTeams, setBulkTeams] = useState<string[]>([]);
  const teamsEnabled = can('users:manage') && companyHasPlanFeature(company, 'scheduling');
  useEffect(() => {
    setBulkPeople([]);
    setBulkTeams([]);
    setBulkOpen(false);
  }, [currentLocation?.id]);
  const saved = useQuery({ queryKey: ['saved-teams'], queryFn: () => savedTeamsApi.list(), enabled: teamsEnabled });
  const [form, setForm] = useState(() => ({
    fullName: '',
    email: '',
    role: 'estimator',
    password: '',
    locationIds: [] as string[],
    locationRoles: {} as Record<string, LocationRole>,
  }));
  /** Whether the add form's per-location roles disclosure is open. */
  const [formRolesOpen, setFormRolesOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  /** The member whose location list is open for editing, if any. */
  const [editingLocationsFor, setEditingLocationsFor] = useState<string | null>(null);
  const [editingHoursFor, setEditingHoursFor] = useState<string | null>(null);

  const canManage = can('users:manage');
  const team = useQuery({ queryKey: ['company-users'], queryFn: () => companyApi.users(), enabled: canManage });
  // The full company list (owners see everything): names for each member's
  // assignments, and the choices in the pickers below.
  const locationsQuery = useQuery({
    queryKey: ['locations'],
    queryFn: () => locationApi.list(),
    enabled: canManage,
  });

  const allLocations: Location[] = locationsQuery.data?.locations ?? [];
  const activeLocations = allLocations.filter((location) => location.status === 'active');
  const activeLocationIds = new Set(activeLocations.map((location) => location.id));
  // Somebody assigned only to a location that has since been archived — a
  // downgrade locks every branch — can reach nothing until they are moved.
  // The editor has to stay reachable to move them, even though the company
  // is back to a single active location.
  const strandedMembers = (team.data?.items ?? []).some(
    (member: TeamMember) =>
      member.locationIds.length > 0 && !member.locationIds.some((id: string) => activeLocationIds.has(id)),
  );
  // The whole location UI disappears for single-location companies.
  const showLocations = activeLocations.length >= 2 || strandedMembers;
  const locationName = (id: string) => allLocations.find((location) => location.id === id)?.name ?? id;

  function refresh() {
    return queryClient.invalidateQueries({ queryKey: ['company-users'] });
  }

  const formCanJoinTeams = !showLocations || !currentLocation || form.locationIds.includes(currentLocation.id);
  const canJoinTeams = createdAccount
    ? createdAccount.isActive && (!currentLocation || createdAccount.locationIds.includes(currentLocation.id))
    : formCanJoinTeams;

  async function assignAccountTeams(account: TeamMember, teamIds: string[], assertLocation: () => void) {
    if (!teamIds.length) return;
    if (!account.isActive || (addingLocationId && !account.locationIds.includes(addingLocationId))) {
      throw new Error('teamLocationRequired');
    }
    await addEmployeesToTeams([account.id], teamIds, assertLocation);
  }

  const createUser = useMutation({
    mutationFn: async () => {
      const assertLocation = () => {
        if (locationRef.current !== addingLocationId) throw new Error('locationChanged');
      };
      assertLocation();
      if (createdAccount) {
        await assignAccountTeams(createdAccount, selectedTeams, assertLocation);
        return createdAccount;
      }
      const teamIds = formCanJoinTeams ? selectedTeams : [];
      if (!formCanJoinTeams) setSelectedTeams([]);
      const { locationIds, locationRoles, ...rest } = form;
      if (!showLocations) {
        const account = await companyApi.createUser(rest);
        setCreatedAccount(account);
        await assignAccountTeams(account, teamIds, assertLocation);
        return account;
      }
      // Overrides only exist for non-owners, and only at assigned locations.
      const roles = rest.role === 'owner' ? {} : pruneRoles(locationRoles, locationIds);
      const account = await companyApi.createUser({
        ...rest,
        locationIds,
        ...(Object.keys(roles).length > 0 ? { locationRoles: roles } : {}),
      });
      setCreatedAccount(account);
      await assignAccountTeams(account, teamIds, assertLocation);
      return account;
    },
    onSuccess: async () => {
      await refresh();
      setAdding(false);
      setCreatedAccount(null);
      setSelectedTeams([]);
      queryClient.invalidateQueries({ queryKey: ['team'] });
      queryClient.invalidateQueries({ queryKey: ['saved-teams'] });
      queryClient.invalidateQueries({ queryKey: ['series'] });
      setForm({ fullName: '', email: '', role: 'estimator', password: '', locationIds: [], locationRoles: {} });
      setFormRolesOpen(false);
      setError(null);
      setFieldErrors({});
    },
    onError: (caught) => {
      queryClient.invalidateQueries({ queryKey: ['saved-teams'] });
      queryClient.invalidateQueries({ queryKey: ['series'] });
      queryClient.invalidateQueries({ queryKey: ['company-users'] });
      if (caught instanceof ApiRequestError) {
        const accountFields = Object.fromEntries(
          Object.entries(caught.fields).filter(([key]) =>
            ['fullName', 'email', 'password', 'locationIds', 'role'].includes(key),
          ),
        );
        setFieldErrors(accountFields);
        setError(Object.keys(accountFields).length > 0 ? null : caught.message);
      } else {
        setError(
          t(
            caught instanceof Error &&
              ['teamFull', 'teamUnavailable', 'locationChanged', 'teamLocationRequired'].includes(caught.message)
              ? `employeePicker.${caught.message}`
              : 'team.addError',
          ),
        );
      }
    },
  });

  const updateUser = useMutation({
    mutationFn: (input: {
      userId: string;
      role?: string;
      isActive?: boolean;
      locationIds?: string[];
      locationRoles?: Record<string, LocationRole>;
      workDayStartLocal?: string | null;
      workDayEndLocal?: string | null;
      workDays?: number[] | null;
    }) => companyApi.updateUser(input.userId, input),
    onSuccess: async () => {
      await refresh();
      setEditingLocationsFor(null);
      setEditingHoursFor(null);
      // Otherwise the banner from a failed attempt outlives the retry that
      // worked, and the save that just landed still reads as an error.
      setError(null);
    },
    onError: (caught) => setError(caught instanceof ApiRequestError ? caught.message : t('team.updateError')),
  });

  const bulkSave = useMutation({
    mutationFn: () => {
      const scope = locationRef.current;
      return addEmployeesToTeams(bulkPeople, bulkTeams, () => {
        if (scope !== locationRef.current) throw new Error('locationChanged');
      });
    },
    onSuccess: async () => {
      setBulkOpen(false);
      setBulkPeople([]);
      setBulkTeams([]);
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['saved-teams'] });
      await queryClient.invalidateQueries({ queryKey: ['series'] });
    },
    onError: (caught) => {
      queryClient.invalidateQueries({ queryKey: ['saved-teams'] });
      queryClient.invalidateQueries({ queryKey: ['series'] });
      setError(
        caught instanceof ApiRequestError
          ? caught.message
          : t(
              caught instanceof Error && ['teamFull', 'teamUnavailable', 'locationChanged'].includes(caught.message)
                ? `employeePicker.${caught.message}`
                : 'savedTeams.saveError',
            ),
      );
    },
  });
  if (!canManage) return null;
  const matching = (team.data?.items ?? []).filter(
    (member) =>
      `${member.fullName} ${member.email}`.toLocaleLowerCase().includes(search.toLocaleLowerCase()) &&
      (!rosterLocation || member.locationIds.includes(rosterLocation)) &&
      (!status || member.isActive === (status === 'active')),
  );
  const pages = Math.max(1, Math.ceil(matching.length / 12));
  const currentPage = Math.min(page, pages - 1);
  const visibleIds = new Set(matching.slice(currentPage * 12, currentPage * 12 + 12).map((member) => member.id));
  const teamChoices = (saved.data?.items ?? [])
    .filter((entry) => entry.isActive)
    .map((entry) => ({
      id: entry.id,
      name: entry.name,
      detail: t('savedTeams.chip', { name: entry.name, count: entry.memberIds.length }),
      disabled: entry.memberIds.length >= 20,
    }));

  const planId = company && isPlanId(company.effectivePlan) ? company.effectivePlan : null;
  const activeSeats = (team.data?.items ?? []).filter((member) => member.isActive).length;
  // The ceiling is the effective plan's, so a live trial (effective plan
  // Business, no seat cap) still shows the button. Once the trial has ended
  // and the plan is Free, one active person is the limit.
  const seatOpen = planId === null || planAllowsAnotherSeat(planId, activeSeats);
  const seatsKnown = team.isSuccess;

  function openAddForm() {
    // A new member starts wherever the person adding them is working — the
    // same default the server applies when no list is sent.
    setForm((current) => ({
      ...current,
      locationIds: currentLocation ? [currentLocation.id] : [],
    }));
    setAddingLocationId(currentLocation?.id);
    setCreatedAccount(null);
    setSelectedTeams([]);
    setAdding(true);
  }

  function toggleFormLocation(locationId: string) {
    if (locationId === currentLocation?.id && form.locationIds.includes(locationId)) setSelectedTeams([]);
    setForm((current) => {
      const locationIds = current.locationIds.includes(locationId)
        ? current.locationIds.filter((id) => id !== locationId)
        : [...current.locationIds, locationId];
      // An override cannot outlive its assignment.
      return { ...current, locationIds, locationRoles: pruneRoles(current.locationRoles, locationIds) };
    });
  }

  function setFormLocationRole(locationId: string, role: LocationRole | '') {
    setForm((current) => {
      const locationRoles = { ...current.locationRoles };
      if (role === '') delete locationRoles[locationId];
      else locationRoles[locationId] = role;
      return { ...current, locationRoles };
    });
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setFieldErrors({});
    if (!createdAccount && showLocations && form.locationIds.length === 0) {
      setFieldErrors({ locationIds: t('team.locationsRequired') });
      return;
    }
    createUser.mutate();
  }

  return (
    <section className="card mb-6" aria-labelledby="team-heading">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 id="team-heading" className="text-lg font-semibold">
          {t('team.heading')}
        </h2>
        {seatsKnown && seatOpen && !adding ? <Button onClick={openAddForm}>{t('team.addMember')}</Button> : null}
      </div>

      {seatsKnown && !seatOpen && planId && !adding ? (
        <div className="mb-4">
          <Alert tone="info">
            <p>{t('team.seatLimit', { plan: PLANS[planId].name, count: PLANS[planId].maxSeats ?? activeSeats })}</p>
            <p className="mt-2">
              <Link to="/settings#settings-billing" className="font-semibold text-[color:var(--color-brand)] underline">
                {t('team.seatLimitCta')}
              </Link>
            </p>
          </Alert>
        </div>
      ) : null}

      {error ? (
        <div className="mb-4">
          <Alert tone="error">{error}</Alert>
        </div>
      ) : null}

      <p className="field-hint mb-4">{t('employeePicker.rosterHint')}</p>
      {adding ? (
        <form
          onSubmit={handleSubmit}
          noValidate
          className="mb-6 rounded-md border border-[color:var(--color-line)] p-4"
        >
          {createdAccount ? <Alert tone="info">{t('employeePicker.createdRetry')}</Alert> : null}
          <fieldset disabled={createUser.isPending || Boolean(createdAccount)}>
            <div className="grid gap-x-4 sm:grid-cols-2">
              <TextInput
                label={t('team.theirName')}
                required
                value={form.fullName}
                error={fieldErrors.fullName}
                onChange={(event) => setForm((current) => ({ ...current, fullName: event.target.value }))}
              />
              <TextInput
                label={t('team.email')}
                type="email"
                required
                value={form.email}
                error={fieldErrors.email}
                onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))}
              />
            </div>

            <Select
              label={t('team.role_')}
              value={form.role}
              hint={t(`team.roleHint.${form.role as UserRole}`)}
              onChange={(event) => setForm((current) => ({ ...current, role: event.target.value }))}
              options={USER_ROLES.map((role) => ({ value: role, label: t(`team.role.${role}`) }))}
            />

            {showLocations ? (
              <fieldset className="mb-4">
                <legend className="field-label">{t('team.locationsLabel')}</legend>
                <p className="field-hint mb-2">{t('team.locationsHint')}</p>
                {activeLocations.map((location) => (
                  <Checkbox
                    key={location.id}
                    label={location.name}
                    checked={form.locationIds.includes(location.id)}
                    onChange={() => toggleFormLocation(location.id)}
                  />
                ))}
                {fieldErrors.locationIds ? (
                  <p className="field-error" role="alert">
                    {fieldErrors.locationIds}
                  </p>
                ) : null}

                {/* Per-location roles, folded away: most members hold their
                  company role everywhere, so the form stays simple unless the
                  owner asks for the exception. Owners have no exceptions. */}
                {form.role !== 'owner' ? (
                  <div className="mt-2">
                    <button
                      type="button"
                      className="text-sm font-semibold text-[color:var(--color-brand)] hover:underline"
                      aria-expanded={formRolesOpen}
                      onClick={() => setFormRolesOpen((open) => !open)}
                    >
                      {formRolesOpen ? t('team.hidePerLocationRoles') : t('team.showPerLocationRoles')}
                    </button>
                    {formRolesOpen ? (
                      <div className="mt-2 rounded-md border border-[color:var(--color-line)] p-3">
                        <p className="field-hint mb-2">{t('team.perLocationRolesHint')}</p>
                        {form.locationIds.length === 0 ? (
                          <p className="text-sm text-[color:var(--color-muted)]">{t('team.perLocationRolesNoneYet')}</p>
                        ) : (
                          activeLocations
                            .filter((location) => form.locationIds.includes(location.id))
                            .map((location) => (
                              <LocationRoleSelect
                                key={location.id}
                                locationName={location.name}
                                companyRole={form.role as UserRole}
                                value={form.locationRoles[location.id] ?? ''}
                                onChange={(role) => setFormLocationRole(location.id, role)}
                              />
                            ))
                        )}
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <p className="field-hint mt-2">{t('team.ownerEverywhereHint')}</p>
                )}
              </fieldset>
            ) : null}

            <TextInput
              label={t('team.startingPassword')}
              type="password"
              required
              minLength={12}
              autoComplete="new-password"
              hint={t('team.startingPasswordHint')}
              value={form.password}
              error={fieldErrors.password}
              onChange={(event) => setForm((current) => ({ ...current, password: event.target.value }))}
            />
          </fieldset>
          {teamsEnabled && saved.isSuccess && (canJoinTeams || (createdAccount && selectedTeams.length > 0)) ? (
            <>
              <p className="field-hint mb-2">
                {t('employeePicker.teamsScope', { location: currentLocation?.name ?? '' })}
              </p>
              <CheckboxPicker
                label={t('employeePicker.optionalTeams')}
                searchLabel={t('employeePicker.searchTeams')}
                choices={teamChoices.map((choice) => ({ ...choice, disabled: choice.disabled || !canJoinTeams }))}
                value={selectedTeams}
                onChange={setSelectedTeams}
                disabled={createUser.isPending}
              />
              {selectedTeams.length ? <Alert tone="info">{t('employeePicker.impact')}</Alert> : null}
            </>
          ) : null}
          {teamsEnabled && saved.isError ? (
            <Alert tone="error">
              <p>{t('savedTeams.loadError')}</p>
              <Button className="mt-3" variant="secondary" busy={saved.isFetching} onClick={() => void saved.refetch()}>
                {t('common.retry')}
              </Button>
            </Alert>
          ) : null}
          <div className="mt-4 flex flex-wrap gap-3">
            <Button type="submit" variant="primary" busy={createUser.isPending}>
              {createdAccount ? t('employeePicker.retry') : t('team.addToTeam')}
            </Button>
            <Button
              disabled={createUser.isPending}
              onClick={() => {
                setAdding(false);
                setCreatedAccount(null);
                setSelectedTeams([]);
                refresh();
              }}
            >
              {t('common.cancel')}
            </Button>
          </div>
        </form>
      ) : null}

      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        <TextInput
          label={t('employeePicker.rosterSearch')}
          type="search"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(0);
          }}
        />
        <Select
          label={t('employeePicker.location')}
          value={rosterLocation}
          onChange={(event) => {
            setRosterLocation(event.target.value);
            setPage(0);
          }}
          options={[
            { value: '', label: t('employeePicker.allLocations') },
            ...activeLocations.map((entry) => ({ value: entry.id, label: entry.name })),
          ]}
        />
        <Select
          label={t('employeePicker.status')}
          value={status}
          onChange={(event) => {
            setStatus(event.target.value);
            setPage(0);
          }}
          options={[
            { value: '', label: t('employeePicker.allStatuses') },
            { value: 'active', label: t('employeePicker.active') },
            { value: 'inactive', label: t('employeePicker.inactive') },
          ]}
        />
      </div>
      {teamsEnabled && saved.isSuccess ? (
        <>
          <div className="my-3 rounded-md border border-[color:var(--color-line)] bg-[color:var(--color-brand-soft)] p-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <strong role="status">{t('employeePicker.selected', { count: bulkPeople.length })}</strong>
              <p className="field-hint">{t('employeePicker.limit', { count: 20 })}</p>
              <Button disabled={!bulkPeople.length || bulkSave.isPending} onClick={() => setBulkOpen(!bulkOpen)}>
                {t('employeePicker.bulk')}
              </Button>
            </div>
            {bulkPeople.length ? (
              <ul className="max-h-48 overflow-y-auto" aria-label={t('employeePicker.selectedList')}>
                {bulkPeople.map((id) => {
                  const name =
                    team.data?.items.find((member) => member.id === id)?.fullName ?? t('employeePicker.unavailable');
                  return (
                    <li key={id} className="flex items-center justify-between gap-3">
                      <span className="min-w-0 break-words">{name}</span>
                      <Button
                        variant="ghost"
                        aria-label={t('employeePicker.remove', { name })}
                        disabled={bulkSave.isPending}
                        onClick={() => setBulkPeople(bulkPeople.filter((entry) => entry !== id))}
                      >
                        ×
                      </Button>
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </div>
          {bulkOpen ? (
            <div className="my-4 rounded-md border border-[color:var(--color-line)] p-4">
              <CheckboxPicker
                label={t('employeePicker.optionalTeams')}
                searchLabel={t('employeePicker.searchTeams')}
                choices={teamChoices}
                value={bulkTeams}
                onChange={setBulkTeams}
                disabled={bulkSave.isPending}
              />
              <Alert tone="info">{t('employeePicker.impact')}</Alert>
              <div className="mt-3 flex flex-wrap gap-3">
                <Button busy={bulkSave.isPending} disabled={!bulkTeams.length} onClick={() => bulkSave.mutate()}>
                  {t('savedTeams.save')}
                </Button>
                <Button disabled={bulkSave.isPending} onClick={() => setBulkOpen(false)}>
                  {t('common.cancel')}
                </Button>
              </div>
            </div>
          ) : null}
        </>
      ) : null}
      {team.isLoading ? <p role="status">{t('employeePicker.loading')}</p> : null}
      {team.isError ? (
        <Alert tone="error">
          <p>{t('employeePicker.loadError')}</p>
          <Button className="mt-3" variant="secondary" busy={team.isFetching} onClick={() => void team.refetch()}>
            {t('common.retry')}
          </Button>
        </Alert>
      ) : null}
      {team.data ? (
        <p className="field-hint mt-3" role="status">
          {t('employeePicker.rosterCount', { count: matching.length })}
        </p>
      ) : null}
      {team.isSuccess && matching.length === 0 ? (
        <p className="field-hint py-3">{t('employeePicker.noMatches')}</p>
      ) : null}
      <ul className="divide-y divide-[color:var(--color-line)]">
        {(team.data?.items ?? []).map((member) => {
          const isSelf = member.id === user?.id;
          const memberRoles = member.locationRoles ?? {};
          return (
            <li key={member.id} hidden={!visibleIds.has(member.id)} className="py-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex min-w-0 items-start gap-3">
                  {teamsEnabled && saved.isSuccess ? (
                    <input
                      type="checkbox"
                      className="mt-1 h-5 w-5 shrink-0 accent-[color:var(--color-brand)]"
                      aria-label={t('employeePicker.select', { name: member.fullName })}
                      checked={bulkPeople.includes(member.id)}
                      disabled={
                        bulkSave.isPending ||
                        !member.isActive ||
                        (Boolean(currentLocation) && !member.locationIds.includes(currentLocation!.id)) ||
                        (!bulkPeople.includes(member.id) && bulkPeople.length >= 20)
                      }
                      onChange={(event) =>
                        setBulkPeople(
                          event.target.checked
                            ? [...bulkPeople, member.id]
                            : bulkPeople.filter((id) => id !== member.id),
                        )
                      }
                    />
                  ) : null}
                  <div className="min-w-0">
                    <p className="font-medium">
                      {member.fullName}
                      {isSelf ? (
                        <span className="ml-2 text-sm text-[color:var(--color-muted)]">{t('team.you')}</span>
                      ) : null}
                      {!member.isActive ? (
                        <span className="ml-2 badge border-[color:var(--color-muted)] text-[color:var(--color-muted)]">
                          {t('team.deactivated')}
                        </span>
                      ) : null}
                    </p>
                    <p className="text-sm text-[color:var(--color-muted)]">{member.email}</p>
                    {showLocations ? (
                      <p className="text-sm text-[color:var(--color-muted)]">
                        {t('team.memberLocations', {
                          names:
                            member.locationIds
                              .map((id) => {
                                const override = memberRoles[id];
                                // "North (Manager)" where the role differs there.
                                return override
                                  ? `${locationName(id)} (${t(`team.role.${override}`)})`
                                  : locationName(id);
                              })
                              .join(', ') || '—',
                        })}
                      </p>
                    ) : null}
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <div className="w-44">
                    <label className="sr-only" htmlFor={`role-${member.id}`}>
                      {t('team.roleFor', { name: member.fullName })}
                    </label>
                    <select
                      id={`role-${member.id}`}
                      className="field-control"
                      value={member.role}
                      // Changing your own role is how a company locks itself out.
                      disabled={isSelf || updateUser.isPending}
                      // A slip in the phone's picker would otherwise re-role
                      // someone (even to Owner) on the spot. Declining leaves
                      // the controlled select on their current role.
                      onChange={(event) => {
                        const role = event.target.value as UserRole;
                        if (
                          window.confirm(
                            t('team.confirmRoleChange', { name: member.fullName, role: t(`team.role.${role}`) }),
                          )
                        ) {
                          updateUser.mutate({ userId: member.id, role });
                        }
                      }}
                    >
                      {USER_ROLES.map((role) => (
                        <option key={role} value={role}>
                          {t(`team.role.${role}`)}
                        </option>
                      ))}
                    </select>
                  </div>

                  {showLocations ? (
                    <Button
                      onClick={() => setEditingLocationsFor((current) => (current === member.id ? null : member.id))}
                      aria-expanded={editingLocationsFor === member.id}
                    >
                      {t('team.editLocations')}
                    </Button>
                  ) : null}

                  <Button
                    onClick={() => setEditingHoursFor((current) => (current === member.id ? null : member.id))}
                    aria-expanded={editingHoursFor === member.id}
                  >
                    {t('team.editHours')}
                  </Button>

                  {!isSelf ? (
                    <Button
                      variant={member.isActive ? 'danger' : 'secondary'}
                      busy={updateUser.isPending}
                      onClick={() => {
                        if (
                          member.isActive &&
                          !window.confirm(t('team.confirmDeactivate', { name: member.fullName }))
                        ) {
                          return;
                        }
                        updateUser.mutate({ userId: member.id, isActive: !member.isActive });
                      }}
                    >
                      {member.isActive ? t('team.deactivate') : t('team.reactivate')}
                    </Button>
                  ) : null}
                </div>
              </div>

              {editingHoursFor === member.id && company ? (
                <MemberHoursEditor
                  member={member}
                  busy={updateUser.isPending}
                  inherited={inheritedHoursFor(member, allLocations, {
                    startLocal: company.workDayStartLocal,
                    endLocal: company.workDayEndLocal,
                    days: company.workDays,
                  })}
                  onSave={(hours) => updateUser.mutate({ userId: member.id, ...hours })}
                  onCancel={() => setEditingHoursFor(null)}
                />
              ) : null}

              {showLocations && editingLocationsFor === member.id ? (
                <MemberLocationsEditor
                  member={member}
                  locations={activeLocations}
                  busy={updateUser.isPending}
                  onSave={(locationIds, locationRoles) =>
                    updateUser.mutate({
                      userId: member.id,
                      locationIds,
                      // Owners carry no overrides; the API refuses them anyway.
                      ...(member.role === 'owner' ? {} : { locationRoles }),
                    })
                  }
                  onCancel={() => setEditingLocationsFor(null)}
                />
              ) : null}
            </li>
          );
        })}
      </ul>
      {pages > 1 ? (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
          <Button disabled={currentPage === 0} onClick={() => setPage(currentPage - 1)}>
            {t('employeePicker.previous')}
          </Button>
          <span>{t('employeePicker.page', { page: currentPage + 1, pages, count: matching.length })}</span>
          <Button disabled={currentPage + 1 >= pages} onClick={() => setPage(currentPage + 1)}>
            {t('employeePicker.next')}
          </Button>
        </div>
      ) : null}
      <p className="field-hint mt-4">{t('team.roleChangeNote')}</p>
    </section>
  );
}

/**
 * One location's role picker: "Company role (…)" is the default and stores
 * nothing; picking a role stores an override for that location only.
 */
function LocationRoleSelect({
  locationName,
  companyRole,
  value,
  onChange,
  disabled,
}: {
  locationName: string;
  companyRole: UserRole;
  value: LocationRole | '';
  onChange: (role: LocationRole | '') => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <Select
      label={t('team.roleAtLocation', { name: locationName })}
      value={value}
      disabled={disabled}
      options={[
        { value: '', label: t('team.companyRoleOption', { role: t(`team.role.${companyRole}`) }) },
        ...LOCATION_ROLES.map((role) => ({ value: role, label: t(`team.role.${role}`) })),
      ]}
      onChange={(event) => onChange(event.target.value as LocationRole | '')}
    />
  );
}

/**
 * The per-member location picker. Min one: the server refuses an empty list,
 * because a member with nowhere to work can sign in and see nothing.
 *
 * Each assigned location also carries a role picker (for non-owners): the
 * default is the company role, and choosing anything else stores an override
 * for that location alone. The full override map is sent on save, so putting
 * a location back on "Company role" clears its entry.
 */
function MemberLocationsEditor({
  member,
  locations,
  busy,
  onSave,
  onCancel,
}: {
  member: TeamMember;
  locations: Location[];
  busy: boolean;
  onSave: (locationIds: string[], locationRoles: Record<string, LocationRole>) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<string[]>(() => member.locationIds);
  const [roles, setRoles] = useState<Record<string, LocationRole>>(() => ({ ...(member.locationRoles ?? {}) }));

  const isOwner = member.role === 'owner';

  function toggle(locationId: string) {
    const next = selected.includes(locationId) ? selected.filter((id) => id !== locationId) : [...selected, locationId];
    setSelected(next);
    // An override cannot outlive its assignment.
    setRoles((currentRoles) => pruneRoles(currentRoles, next));
  }

  function setRole(locationId: string, role: LocationRole | '') {
    setRoles((current) => {
      const next = { ...current };
      if (role === '') delete next[locationId];
      else next[locationId] = role;
      return next;
    });
  }

  return (
    <div className="mt-3 rounded-md border border-[color:var(--color-line)] p-4">
      <p className="field-label">{t('team.locationsFor', { name: member.fullName })}</p>
      <p className="field-hint mb-2">{t('team.locationsHint')}</p>
      {isOwner ? <p className="field-hint mb-2">{t('team.ownerEverywhereHint')}</p> : null}
      {locations.map((location) => {
        const assigned = selected.includes(location.id);
        return (
          <div key={location.id}>
            <Checkbox label={location.name} checked={assigned} onChange={() => toggle(location.id)} />
            {!isOwner && assigned ? (
              <div className="mb-1 ml-8 max-w-xs">
                <LocationRoleSelect
                  locationName={location.name}
                  companyRole={member.role as UserRole}
                  value={roles[location.id] ?? ''}
                  onChange={(role) => setRole(location.id, role)}
                  disabled={busy}
                />
              </div>
            ) : null}
          </div>
        );
      })}
      {!isOwner ? <p className="field-hint mt-1">{t('team.perLocationRolesHint')}</p> : null}
      {selected.length === 0 ? (
        <p className="field-error" role="alert">
          {t('team.locationsRequired')}
        </p>
      ) : null}
      <div className="mt-2 flex gap-3">
        <Button
          variant="primary"
          busy={busy}
          disabled={selected.length === 0}
          onClick={() => onSave(selected, pruneRoles(roles, selected))}
        >
          {t('team.saveLocations')}
        </Button>
        <Button onClick={onCancel}>{t('common.cancel')}</Button>
      </div>
    </div>
  );
}

/**
 * One person's working hours.
 *
 * Empty means "whatever their branch works", which is why the inherited
 * values sit in the placeholders instead of being copied into the fields: a
 * blank start time is not midnight, it is "the same as everyone else there".
 * The days behave the same way — while inheriting they show the company's
 * set, so the first click starts from the week this person actually works.
 */
function MemberHoursEditor({
  member,
  inherited,
  busy,
  onSave,
  onCancel,
}: {
  member: TeamMember;
  inherited: InheritedHours;
  busy: boolean;
  onSave: (hours: {
    workDayStartLocal: string | null;
    workDayEndLocal: string | null;
    workDays: number[] | null;
  }) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [startLocal, setStartLocal] = useState(member.workDayStartLocal ?? '');
  const [endLocal, setEndLocal] = useState(member.workDayEndLocal ?? '');
  const [days, setDays] = useState<string>(member.workDays ?? '');

  const inheritingDays = days === '';
  const shownDays = parseWorkDays(inheritingDays ? inherited.days : days);

  return (
    <div className="mt-3 rounded-md border border-[color:var(--color-line)] p-4">
      <p className="field-label">{t('team.hoursFor', { name: member.fullName })}</p>
      <p className="field-hint mb-2">{t('team.hoursHint')}</p>
      {inherited.branchesDiffer ? <p className="field-hint mb-2">{t('team.hoursBranchesDiffer')}</p> : null}
      {inherited.branchesArchived ? <p className="field-hint mb-2">{t('team.hoursBranchesArchived')}</p> : null}
      <div className="grid gap-x-4 sm:grid-cols-2">
        <TextInput
          label={t('settings.workDayStart')}
          type="time"
          step={300}
          value={startLocal}
          placeholder={inherited.startLocal}
          hint={t('team.hoursInheritHint', { value: formatLocalTime(inherited.startLocal) })}
          disabled={busy}
          onChange={(event) => setStartLocal(event.target.value)}
        />
        <TextInput
          label={t('settings.workDayEnd')}
          type="time"
          step={300}
          value={endLocal}
          placeholder={inherited.endLocal}
          hint={t('team.hoursInheritHint', { value: formatLocalTime(inherited.endLocal) })}
          disabled={busy}
          onChange={(event) => setEndLocal(event.target.value)}
        />
      </div>
      <fieldset className="mb-2">
        <legend className="field-label">{t('settings.workDaysLabel')}</legend>
        <div className="flex flex-wrap gap-2">
          {[0, 1, 2, 3, 4, 5, 6].map((day) => {
            const active = shownDays.includes(day);
            return (
              <button
                key={day}
                type="button"
                role="checkbox"
                aria-checked={active}
                disabled={busy}
                onClick={() => {
                  const next = active ? shownDays.filter((d) => d !== day) : [...shownDays, day].sort((a, b) => a - b);
                  if (next.length > 0) setDays(next.join(','));
                }}
                className={
                  'rounded-full border px-3 py-1.5 text-sm font-medium disabled:opacity-60 ' +
                  (active
                    ? 'border-[color:var(--color-brand)] bg-[color:var(--color-brand-soft)] text-[color:var(--color-brand-strong)]'
                    : 'border-[color:var(--color-line)] text-[color:var(--color-muted)]') +
                  (inheritingDays ? ' opacity-70' : '')
                }
              >
                {t(`schedule.dayOfWeek.${day}`)}
              </button>
            );
          })}
        </div>
        <p className="field-hint mt-2">{inheritingDays ? t('team.daysInheriting') : t('team.daysOwn')}</p>
      </fieldset>
      <div className="mt-2 flex gap-3">
        <Button
          variant="primary"
          busy={busy}
          onClick={() =>
            onSave({
              workDayStartLocal: startLocal.trim() === '' ? null : startLocal,
              workDayEndLocal: endLocal.trim() === '' ? null : endLocal,
              workDays: days === '' ? null : parseWorkDays(days),
            })
          }
        >
          {t('team.saveHours')}
        </Button>
        <Button
          onClick={() => {
            setStartLocal('');
            setEndLocal('');
            setDays('');
          }}
          disabled={busy || (startLocal === '' && endLocal === '' && days === '')}
        >
          {t('team.followBranch')}
        </Button>
        <Button onClick={onCancel}>{t('common.cancel')}</Button>
      </div>
    </div>
  );
}

interface InheritedHours {
  startLocal: string;
  endLocal: string;
  days: string;
  /** Their branches do not agree, so the company's values stand in. */
  branchesDiffer: boolean;
  /** Every branch they are assigned to is archived. */
  branchesArchived: boolean;
}

/**
 * What this member's hours fall back to when they keep none of their own.
 *
 * Their BRANCH, not the company: a person's override layers on where they
 * work, so seeding the editor from the company would show a Saturday branch
 * as closed on Saturday — and the first click would then save a personal
 * override built from the wrong week, quietly taking Saturday off them.
 *
 * Somebody who works several branches has no single answer. Where those
 * branches agree there is one anyway; where they disagree the company's
 * values stand in and the editor says so, because inventing one branch's
 * hours as "theirs" would be a guess presented as a fact.
 */
function inheritedHoursFor(
  member: TeamMember,
  locations: LocationWithIntakeLink[],
  companyHours: { startLocal: string; endLocal: string; days: string },
): InheritedHours {
  // Archived branches included on purpose. A member assigned only to a shut
  // one still belongs to its week, and that is the week an override saved
  // here will apply to the day it reopens — seeding from the company would
  // hand them the wrong schedule with the branch's return.
  const theirs = locations.filter((location) => member.locationIds.includes(location.id));
  if (theirs.length === 0) return { ...companyHours, branchesDiffer: false, branchesArchived: false };
  const branchesArchived = theirs.every((location) => location.status !== 'active');

  const windows = theirs.map((location) => ({
    startLocal: location.effective.workDayStartLocal,
    endLocal: location.effective.workDayEndLocal,
    days: location.effective.workDays,
  }));
  const first = windows[0]!;
  const agree = windows.every(
    (window) =>
      window.startLocal === first.startLocal && window.endLocal === first.endLocal && window.days === first.days,
  );
  return agree
    ? { ...first, branchesDiffer: false, branchesArchived }
    : { ...companyHours, branchesDiffer: true, branchesArchived };
}
